import { styled, Skeleton } from '@mui/material';
import styles from 'src/components/BeaconV2Components/styles/styles';
import React from 'react';
import { useSelector } from 'react-redux';
import { getStrategyDisplayInfo } from 'src/components/AdManager/AmsUtils';
import { EntityLink } from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import ReduxStore from 'src/types/store/reduxStore';
import _startCase from 'lodash/startCase';
import DailyCampaignBudget from 'src/components/BeaconV2Components/GenericSidebarNav/AdvertisingEditFields/DailyCampaignBudget';
import PortfolioBudget from 'src/components/BeaconV2Components/GenericSidebarNav/AdvertisingEditFields/PortfolioBudget';
import PortfolioOptimization from 'src/components/BeaconV2Components/GenericSidebarNav/AdvertisingEditFields/PortfolioOptimization';
import PortfolioEdit from 'src/components/BeaconV2Components/GenericSidebarNav/AdvertisingEditFields/PortfolioEdit';
import { checkInstacartRetailer } from 'src/utils/app';
import MinimumCampaignBudget from 'src/components/BeaconV2Components/GenericSidebarNav/AdvertisingEditFields/MinimumCampaignBudget';

const Container = styled('div')({
  marginTop: 32,
  padding: '0',
  textAligh: 'left',

  '.field-title': {
    fontSize: 12,
    fontWeight: 'bold',
    lineHeight: 1.6,
    letterSpacing: 0.8,
    textTransform: 'uppercase'
  },
  'div:not(:last-child) .field-subtitle': {
    ...styles.clampTextOverflow2,
    fontSize: 14,
    lineHeight: 1.33,
    margin: '2px 0 20px'
  },
  '.no-margin': {
    margin: '0px !important'
  },
  '.overflow-scroll': {
    ...styles.hiddenScrollbar,
    overflow: 'scroll'
  }
});

const isDelivering = (entity: any) => entity.extendedAttributes.statusDerived === 'delivering';

const getEntityDetails = (
  entity: ReduxStore['entityService']['mainEntity'],
  adCampaigns: ReduxStore['adCampaigns'],
  adEntities: ReduxStore['adEntities'],
  adPortfolios: ReduxStore['adPortfolios']
) => {
  const entityDetails = [];

  switch (entity.type) {
    case 'client': {
      const activeEntities = adEntities.filter((e: any) => isDelivering(e));
      const activeCampaigns = adCampaigns.filter((c: any) => isDelivering(c));
      const activePortfolios = adPortfolios.filter((p: any) => isDelivering(p));
      entityDetails.push({ title: 'Entities', value: `${activeEntities.length} active` });
      if (!checkInstacartRetailer()) {
        entityDetails.push({ title: 'Portfolios', value: `${activePortfolios.length} active` });
      }
      entityDetails.push({ title: 'Campaigns', value: `${activeCampaigns.length} active` });
      break;
    }
    case 'adEntity': {
      const activeCampaigns = adCampaigns.filter(
        (c: any) => c.extendedAttributes.entityId === entity.id && isDelivering(c)
      );
      const activePortfolios = adPortfolios.filter(
        (p: any) => p.extendedAttributes.entityId === entity.id && isDelivering(p)
      );
      entityDetails.push({ title: 'Entity Id', value: entity.id });
      if (!checkInstacartRetailer()) {
        entityDetails.push({ title: 'Portfolios', value: `${activePortfolios.length} active` });
      }
      entityDetails.push({ title: 'Campaigns', value: `${activeCampaigns.length} active` });
      break;
    }
    case 'adPortfolio': {
      const adEntity = adEntities.find((e) => e.id === entity?.extendedAttributes?.entityId);
      const activeCampaigns = adCampaigns.filter(
        (c: any) => c.extendedAttributes?.portfolioId === entity.id && isDelivering(c)
      );
      entityDetails.push({ title: 'Entity', value: adEntity?.name, linkId: adEntity?.id, entityType: 'adEntity' });
      entityDetails.push({ title: 'Entity Id', value: adEntity?.id, linkId: adEntity?.id, entityType: 'adEntity' });
      entityDetails.push({ title: 'Portfolio Id', value: entity.id });
      entityDetails.push({ title: 'Status', value: _startCase(entity.extendedAttributes.statusDerived || '--') });
      entityDetails.push({ title: 'Campaigns', value: `${activeCampaigns.length} active` });

      break;
    }
    case 'product': {
      const campaignSet = new Set([...entity?.adProductEntity.campaignIds]);
      const activeEntities = adEntities.filter((e: any) => isDelivering(e));
      const activeCampaigns = adCampaigns.filter((c: any) => isDelivering(c) && campaignSet.has(c.campaignId));
      const activePortfolios = activeCampaigns.reduce((acc: Set<string>, p: any) => {
        if (p.extendedAttributes?.portfolioId !== 'unassigned') {
          acc.add(p.extendedAttributes?.portfolioId);
        }
        return acc;
      }, new Set<string>());

      const status = entity.availabilityStatus;

      entityDetails.push({ title: 'RETAILER SKU', value: entity.retailerSku });
      entityDetails.push({
        title: 'Status',
        value: status ?? '--'
      });
      entityDetails.push({ title: 'Entities', value: `${activeEntities.length} active` });
      if (!checkInstacartRetailer()) {
        entityDetails.push({ title: 'Portfolios', value: `${activePortfolios.size} active` });
      }
      entityDetails.push({ title: 'Campaigns', value: `${activeCampaigns.length} active` });

      break;
    }
    case 'adCampaign': {
      // @ts-ignore
      const adEntity = adEntities.find((e) => e.id === entity?.entityId);
      const adPortfolio = adPortfolios.find((p) => p.id === entity?.extendedAttributes?.portfolioId);
      entityDetails.push({ title: 'Entity', value: adEntity?.name, linkId: adEntity?.id, entityType: 'adEntity' });
      entityDetails.push({ title: 'Entity Id', value: adEntity?.id, linkId: adEntity?.id, entityType: 'adEntity' });
      if (!checkInstacartRetailer()) {
        entityDetails.push(
          <PortfolioEdit
            title="Portfolio"
            value={adPortfolio?.displayName}
            linkId={adPortfolio?.id}
            // @ts-ignore
            entityType="adPortfolio"
          />
        );
        // remove if instacart
        entityDetails.push({
          title: 'Portfolio Id',
          value: adPortfolio?.id,
          linkId: adPortfolio?.id,
          entityType: 'adPortfolio'
        });
      }

      entityDetails.push({
        title: 'Status',
        value: _startCase(entity.extendedAttributes.statusDerived || '--')
      });

      if (!checkInstacartRetailer()) {
        entityDetails.push({
          title: 'Optimization',
          value: getStrategyDisplayInfo(entity.extendedAttributes).displayName
        });
      }

      break;
    }
    default:
      break;
  }
  return entityDetails;
};

const getEntityEditDetails = (entity: any) => {
  const entityEditDetails = [];
  switch (entity.type) {
    case 'adPortfolio': {
      entityEditDetails.push(PortfolioOptimization);
      entityEditDetails.push(PortfolioBudget);
      break;
    }
    case 'adCampaign': {
      if (!checkInstacartRetailer()) {
        entityEditDetails.push(MinimumCampaignBudget);
        entityEditDetails.push(DailyCampaignBudget);
      }
      break;
    }
    default:
      break;
  }
  return entityEditDetails;
};

const EntityDetails = () => {
  const adEntities = useSelector((state: ReduxStore) => state.adEntities);
  const adPortfolios = useSelector((state: ReduxStore) => state.adPortfolios);

  const adCampaigns = useSelector((state: ReduxStore) => state.adCampaigns);
  const mainEntity = useSelector((state: ReduxStore) => state.entityService.mainEntity);
  const adLoading = useSelector((state: ReduxStore) => state.adManagerLoading);

  const entityDetails = getEntityDetails(mainEntity, adCampaigns, adEntities, adPortfolios);

  const entityEditDetails = getEntityEditDetails(mainEntity);

  if (!entityDetails.length) {
    return null;
  }

  return (
    <Container>
      {entityDetails.map((detail) => {
        if (adLoading) {
          return (
            <div
              key={detail.title}
              style={{
                width: '50%'
              }}
            >
              <Skeleton className="field-title" />
              <Skeleton className="field-subtitle" />
            </div>
          );
        }

        if (React.isValidElement(detail)) {
          return detail;
        }

        return (
          <div key={detail.title}>
            <div className="field-title">{detail.title}</div>
            {detail.linkId ? (
              <EntityLink className="field-subtitle" id={detail.linkId} entityType={detail.entityType}>
                {detail.value}
              </EntityLink>
            ) : (
              <div className="field-subtitle">{detail.value}</div>
            )}
          </div>
        );
      })}

      {entityEditDetails.map((DetailComponent, index) => {
        if (adLoading) {
          return <Skeleton key={index} />;
        }
        return <DetailComponent key={index} />;
      })}
    </Container>
  );
};

export default EntityDetails;
